/**
 * LazyYouTube — render a YouTube thumbnail + play button. Loads the actual
 * iframe ONLY after the user clicks. This keeps the home page lightweight
 * (no 2-3 MB of YouTube scripts on first paint) while still embedding the
 * video. Adds VideoObject JSON-LD so Google indexes it in the Videos tab.
 *
 * Strategy:
 *   - Server-side render: an <a> link to youtu.be + a poster image.
 *   - On click: replace the link with an <iframe> for instant playback.
 *   - This is exactly how lite-youtube-embed works, in <60 LOC.
 */
import { useState } from "react";
import { Play } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function LazyYouTube({
  id,
  title = "Watch how it works",
  description = "Short product walkthrough",
  uploadDate, // ISO date string, e.g. "2026-06-29"
  durationISO = "PT2M", // ISO 8601 — required for Google VideoObject
  thumbnailQuality = "hqdefault", // hqdefault works for ALL videos; maxresdefault only when uploaded ≥1080p
  className = "",
  testId = "lazy-youtube",
}) {
  const [loaded, setLoaded] = useState(false);
  const poster = `https://i.ytimg.com/vi/${id}/${thumbnailQuality}.jpg`;
  const watchUrl = `https://www.youtube.com/watch?v=${id}`;

  // VideoObject schema — gives Google enough data to surface the video in
  // Search ("Videos" carousel) which is far less competitive than web search.
  const videoJsonLd = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: title,
    description,
    thumbnailUrl: [poster, `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`],
    uploadDate: uploadDate || "2026-06-29T00:00:00+00:00",
    duration: durationISO,
    contentUrl: watchUrl,
    embedUrl: `https://www.youtube.com/embed/${id}`,
    publisher: {
      "@type": "Organization",
      name: "CarSaleDoc",
      logo: { "@type": "ImageObject", url: "https://www.carsaledoc.com/og-default.png" },
    },
  };

  return (
    <div className={`relative w-full aspect-video rounded-2xl overflow-hidden bg-black ${className}`} data-testid={testId}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(videoJsonLd)}</script>
      </Helmet>
      {!loaded ? (
        <a
          href={watchUrl}
          onClick={(e) => {
            e.preventDefault();
            setLoaded(true);
          }}
          className="absolute inset-0 group cursor-pointer block"
          aria-label={`Play video: ${title}`}
          data-testid={`${testId}-play`}
        >
          {/* Poster image */}
          <img
            src={poster}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            width="1280"
            height="720"
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent transition-opacity group-hover:opacity-70" />
          {/* Play button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 shadow-2xl flex items-center justify-center transition-transform group-hover:scale-110">
              <Play
                size={28}
                className="text-[var(--csd-primary)] ml-1"
                fill="currentColor"
                strokeWidth={0}
              />
            </div>
          </div>
          {/* Title overlay (bottom-left) */}
          <div className="absolute bottom-3 left-3 right-3 text-white text-xs sm:text-sm font-semibold drop-shadow-lg line-clamp-1">
            {title}
          </div>
        </a>
      ) : (
        <iframe
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  );
}
